import type { Reference } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { notificationContext } from '@context/notifications';
import { useUser } from '@context/user-context';
import { CreateWorkspaceDocument } from '@graphql/mutations/workspace/create.graphql';
import { UpdateWorkspaceDocument } from '@graphql/mutations/workspace/update.graphql';
import { useCheckout, usePrices } from '@hooks/billing-subscription';
import { GetWorkspacesDocument } from '@hooks/workspace/query.graphql';
import { logger } from '@modules/logger';
import { makePageTitle } from '@modules/page-title';
import { Box } from '@ui-lib';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { type ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { showNotification } from 'ui-lib/mantine/notifications';

import { BUSINESS_ONBOARDING_ROUTES, FALLBACK_MIN_UNIT_QUANTITY } from './constants';
import { BusinessOnboardingContext, type StartSubscriptionArgs } from './context';
import { UpdateProfileDocument } from './onboarding.graphql';
import type { ProductNames } from './types';

let externalFirstName = '';
let externalLastName = '';
let externalProductInterests: ProductNames[] = [];

interface BusinessOnboardingFlowOptions {
  beforeCheckout?: (args: { workspaceId: string; profileId?: string }) => Promise<void>;
  getCheckoutUrls?: (args: { billingInterval: 'year' | 'month'; routerPath: string }) => {
    successUrl: string;
    cancelUrl: string;
  };
  onExistingSubscription?: (args: { workspaceId: string }) => Promise<void> | void;
  allowSubscribedUsersOnStartTrial?: boolean;
}

export function BusinessOnboardingLayout({
  children,
  pageTitle,
  flowOptions,
  annualOnly = false,
}: {
  children: ReactNode;
  pageTitle: string;
  flowOptions?: BusinessOnboardingFlowOptions;
  /**
   * When true, forces annual billing and never reveals the interval selector (annual-only experiment
   * treatment). Overridden by a `?interval=month` deeplink, which always opts the user out of the
   * experiment — the blinq.me marketing site still links straight to monthly and must be honoured.
   */
  annualOnly?: boolean;
}): JSX.Element {
  const { prices, products, loading: pricesLoading } = usePrices();

  const teamProduct = products.find((product) => product.type === 'TEAM');
  const minSeatCount = teamProduct?.min_card_count ?? FALLBACK_MIN_UNIT_QUANTITY;
  const initialSeatCount = minSeatCount;

  const user = useUser();

  const { notify } = useContext(notificationContext);

  const router = useRouter();

  const { data: workspacesResult, loading: loadingWorkspaces } = useQuery(GetWorkspacesDocument);

  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [productInterests, setProductInterests] = useState<ProductNames[]>([]);

  const [billingInterval, setBillingInterval] = useState<'year' | 'month'>('year');
  const [showBillingIntervalSelector, setShowBillingIntervalSelector] = useState<boolean>(false);
  const [seats, setSeats] = useState<number>(initialSeatCount);
  const [promotionalCode, setPromotionalCode] = useState<string>('');
  const [checkoutLoading, setCheckoutLoading] = useState<boolean>(false);

  // If the minimum seat count re-calculates (products load), update seat state
  useEffect(() => {
    setSeats(initialSeatCount);
  }, [initialSeatCount]);

  // On mount, initialize component state from external state
  useEffect(() => {
    setFirstName(externalFirstName);
    setLastName(externalLastName);
    setProductInterests(externalProductInterests);
  }, []);

  // Set the billing interval based on the query params
  useEffect(() => {
    if (!router.isReady) {
      return;
    }

    const { interval } = router.query;

    // A ?interval=month deeplink opts the user out of the experiment; annual-only enforcement is
    // skipped so the blinq.me monthly link keeps working. Any other case (interval=year or none)
    // stays in the experiment and treatment forces annual.
    if (annualOnly && interval !== 'month') {
      setBillingInterval('year');
      return;
    }

    const isValidInterval = interval === 'year' || interval === 'month';
    if (isValidInterval) {
      setBillingInterval(interval);
    }
  }, [router.isReady, router.query, annualOnly]);

  useEffect(() => {
    // hide until we know whether to show
    if (!router.isReady) {
      return;
    }

    const { interval: initialInterval, ref_platform, ref_path } = router.query;

    // ?interval=month opts out of the experiment (see billing-interval effect above); everything else
    // stays in treatment, which never reveals the selector.
    if (annualOnly && initialInterval !== 'month') {
      setShowBillingIntervalSelector(false);
      return;
    }

    const referrerIsWebflowPricing = ref_platform === 'webflow' && ref_path === '/pricing';

    setShowBillingIntervalSelector(!referrerIsWebflowPricing || initialInterval === 'month');
  }, [router.isReady, router.query, annualOnly]);

  const [createWorkspace] = useMutation(CreateWorkspaceDocument);
  const [updateWorkspace] = useMutation(UpdateWorkspaceDocument);
  const [updateProfile] = useMutation(UpdateProfileDocument);
  const { startCheckout } = useCheckout();

  /* 
    This useEffect handles authenticated users, and conditionally
    redirects them based on their onboarding status.
  */
  useEffect(() => {
    if (user.loading || loadingWorkspaces) {
      return;
    }

    const isLoggedIn = Boolean(user.actor?.id);
    if (!isLoggedIn) {
      return;
    }

    const isSignUpPage = window.location.href.includes(BUSINESS_ONBOARDING_ROUTES.SIGN_UP);
    if (!isSignUpPage) {
      return;
    }

    const teamWorkspace = workspacesResult?.workspaces?.find((item) => item?.type === 'TEAM');
    const billingSubscription = teamWorkspace?.billing_subscription?.status;

    // If user has a team workspace with a billing subscription then
    // redirect them to the business home route
    if (billingSubscription === 'active' || billingSubscription === 'trialing') {
      showNotification({
        id: 'existing-business-account',
        notificationContent: {
          content: 'Existing business account found, redirecting to business home page',
        },
        tone: 'info',
      });
      router.replace(`/${teamWorkspace?.id}/home`);
      return;
    }

    const queryParams = router.query;

    router.replace({
      pathname: BUSINESS_ONBOARDING_ROUTES.YOUR_NAME,
      query: queryParams,
    });
  }, [loadingWorkspaces, router, user.actor?.id, user.loading, workspacesResult?.workspaces]);

  const handleStartSubscription = async ({
    priceId,
    withTrial,
    adjustableSeats,
    checkoutInterval,
    checkoutPromotionalCode,
    profileId,
    workspaceId,
  }: {
    priceId: string;
    withTrial: boolean;
    adjustableSeats: boolean;
    checkoutInterval: 'year' | 'month';
    checkoutPromotionalCode: string;
    profileId?: string;
    workspaceId: string;
  }) => {
    if (profileId && firstName) {
      updateProfile({
        variables: {
          id: profileId,
          workspace_id: workspaceId,
          input: {
            given_name: firstName,
            family_name: lastName,
          },
        },
      });
    }

    await flowOptions?.beforeCheckout?.({
      workspaceId,
      profileId,
    });

    const checkoutUrls = flowOptions?.getCheckoutUrls?.({
      billingInterval: checkoutInterval,
      routerPath: router.asPath,
    });

    // The direct-to-checkout path selects seats after payment, so it returns to the
    // choose-seats screen; picker-based paths (sales, HubSpot install) keep the old return.
    const successUrl =
      checkoutUrls?.successUrl ??
      (adjustableSeats
        ? `${origin}${BUSINESS_ONBOARDING_ROUTES.CHOOSE_SEATS}?interval=${checkoutInterval}`
        : `${origin}${BUSINESS_ONBOARDING_ROUTES.SETUP_WORKSPACE}`);

    // A cancelled checkout drops the user back mid-flow, so the promo/trial deeplink params must
    // survive the round trip to Stripe for a retried checkout to apply them again.
    const cancelParams = new URLSearchParams({ interval: checkoutInterval });
    if (typeof router.query.promo === 'string' && router.query.promo) {
      cancelParams.set('promo', router.query.promo);
    }

    if (typeof router.query.trial === 'string' && router.query.trial) {
      cancelParams.set('trial', router.query.trial);
    }

    const cancelUrl =
      checkoutUrls?.cancelUrl ??
      (adjustableSeats
        ? `${origin}${BUSINESS_ONBOARDING_ROUTES.PRODUCTS}?${cancelParams}`
        : `${origin}${BUSINESS_ONBOARDING_ROUTES.START_TRIAL}?${cancelParams}`);

    await startCheckout({
      priceId,
      workspaceId,
      // Guard against the pre-product fallback seat state; never send less than the plan minimum.
      seats: Math.max(seats, minSeatCount),
      adjustableSeats,
      promotionalCode: checkoutPromotionalCode,
      withTrial,
      successUrl,
      cancelUrl,
    });
  };

  const startSubscription = async ({
    priceId,
    withTrial = true,
    adjustableSeats = false,
    billingInterval: billingIntervalArg,
    promotionalCode: promotionalCodeArg,
  }: StartSubscriptionArgs) => {
    // Callers that derive the interval from the URL pass it explicitly; the layout's synced
    // state can lag a render behind the query param when this fires from a mount effect.
    const checkoutInterval = billingIntervalArg ?? billingInterval;
    const checkoutPromotionalCode = promotionalCodeArg ?? promotionalCode;

    setCheckoutLoading(true);
    try {
      const workspaces = workspacesResult?.workspaces;
      const existingTeamWorkspace = workspaces?.find((ws) => ws?.type === 'TEAM');

      if (!existingTeamWorkspace) {
        const { data } = await createWorkspace({
          variables: {
            createInput: {
              product_interests: productInterests,
              type: 'TEAM',
            },
          },
          update: (cache, { data }) => {
            // Identify the new workspace ref in the cache
            const teamWorkspaceRef = cache.identify({
              __typename: 'Workspace',
              id: data?.createWorkspace?.workspace.id,
            }) as string;

            // Add this new workspace ref to the actor workspaces array
            cache.modify({
              id: cache.identify({ __typename: 'Actor', id: user.actor?.id }),
              fields: {
                workspaces(existingWorkspaces: readonly Reference[]) {
                  const newWorkspaces = [...existingWorkspaces, { __ref: teamWorkspaceRef }];
                  return newWorkspaces;
                },
              },
            });
          },
        });

        const newTeamWorkspace = data?.createWorkspace?.workspace;
        if (!newTeamWorkspace) {
          throw new Error('An error occurred creating workspace');
        }

        const matchingProfile = data?.createWorkspace?.workspace.profiles?.find(
          (profile) => profile.workspace?.id === newTeamWorkspace.id,
        );

        await handleStartSubscription({
          priceId,
          withTrial,
          adjustableSeats,
          checkoutInterval,
          checkoutPromotionalCode,
          workspaceId: newTeamWorkspace.id,
          profileId: matchingProfile?.id,
        });

        return true;
      }

      const billingSubscription = existingTeamWorkspace.billing_subscription?.status;
      if (!(billingSubscription === 'active' || billingSubscription === 'trialing')) {
        const { data } = await updateWorkspace({
          variables: {
            input: {
              id: existingTeamWorkspace.id,
              product_interests: productInterests,
            },
          },
        });

        const matchingProfile = data?.updateWorkspace?.workspace.profiles?.find(
          (profile) => profile.workspace?.id === existingTeamWorkspace.id,
        );

        await handleStartSubscription({
          priceId,
          withTrial,
          adjustableSeats,
          checkoutInterval,
          checkoutPromotionalCode,
          workspaceId: existingTeamWorkspace.id,
          profileId: matchingProfile?.id,
        });

        return true;
      }

      if (flowOptions?.onExistingSubscription) {
        await flowOptions.onExistingSubscription({
          workspaceId: existingTeamWorkspace.id,
        });
        return true;
      }

      router.replace(BUSINESS_ONBOARDING_ROUTES.SETUP_WORKSPACE);
      return true;
    } catch (e) {
      logger.error('Error starting checkout', { error: e });
      setCheckoutLoading(false);
      notify({
        type: 'info',
        title: 'Error starting checkout',
        timeout: 5000,
      });
      return false;
    }
  };

  const goTo = (pathname: string) => {
    const queryParams = router.query;
    router.push({
      pathname: pathname,
      query: queryParams,
    });
  };

  const checkStepPrerequisites = useCallback(
    (
      step:
        | 'login'
        | 'your-name'
        | 'product-interests'
        | 'start-trial'
        | 'start-subscription'
        | 'setup-workspace',
    ) => {
      if (user.loading) {
        return;
      }

      const teamWorkspace = user.actor?.workspaces?.find((item) => item.type === 'TEAM');
      const hasBillingSubscription = Boolean(teamWorkspace?.billing_subscription);

      const queryParams = router.query;

      const isLoggedIn = Boolean(user.actor?.id);

      switch (step) {
        case 'your-name': {
          if (!isLoggedIn) {
            return router.replace({
              pathname: BUSINESS_ONBOARDING_ROUTES.SIGN_UP,
              query: queryParams,
            });
          }

          break;
        }
        case 'product-interests': {
          if (!isLoggedIn) {
            return router.replace({
              pathname: BUSINESS_ONBOARDING_ROUTES.SIGN_UP,
              query: queryParams,
            });
          }
          break;
        }
        case 'start-trial':
        case 'start-subscription': {
          if (!isLoggedIn) {
            return router.replace({
              pathname: BUSINESS_ONBOARDING_ROUTES.SIGN_UP,
              query: queryParams,
            });
          }

          if (hasBillingSubscription) {
            if (flowOptions?.allowSubscribedUsersOnStartTrial) {
              break;
            }

            return router.replace(BUSINESS_ONBOARDING_ROUTES.SETUP_WORKSPACE);
          }

          break;
        }
        case 'setup-workspace': {
          if (!isLoggedIn) {
            return router.replace({
              pathname: BUSINESS_ONBOARDING_ROUTES.SIGN_UP,
              query: queryParams,
            });
          }

          break;
        }
      }
    },
    [flowOptions?.allowSubscribedUsersOnStartTrial, router, user],
  );

  useEffect(() => {
    return () => {
      externalFirstName = firstName;
      externalLastName = lastName;
      externalProductInterests = productInterests;
    };
  }, [firstName, lastName, productInterests]);
  const constructedPageTitle = makePageTitle(pageTitle);
  return (
    <>
      <Head>
        <title>{constructedPageTitle}</title>
      </Head>
      <BusinessOnboardingContext.Provider
        value={{
          formState: {
            firstName,
            lastName,
            productInterests,
            billingInterval,
            seats,
            promotionalCode,
            minSeatCount,
            setFirstName,
            setLastName,
            setProductInterests,
            setBillingInterval,
            setSeats,
            setPromotionalCode,
          },
          prices,
          pricesLoading,
          checkoutLoading,
          workspacesLoading: loadingWorkspaces,
          startSubscription,
          checkStepPrerequisites,
          goTo,
          flags: {
            showBillingIntervalSelector,
          },
        }}
      >
        <Box display="flex" flexDirection="row">
          {children}
        </Box>
      </BusinessOnboardingContext.Provider>
    </>
  );
}
